import React, { useLayoutEffect, useRef, useState, Suspense, lazy } from 'react';
import { engagementMediaThumbnails } from '../../../assets/weddingImages';
import { styled } from 'styled-components';
import PageHeader from '../../common/PageHeader';

// Lazy load modal content components - these aren't needed until user interaction
const DressCode = lazy(() => import('../../pages/DressCode'));
const FAQ = lazy(() => import('../../pages/FAQ'));
const SlideRSVPFlow = lazy(() => import('../../rsvp-flow/SlideRSVPFlow'));
const EngagementMasonry = lazy(() => import('../../EngagementMasonry').then(m => ({ default: m.EngagementMasonry })));
const HotelsPage = lazy(() => import('../HotelsPage'));

export type ContentId = 'dresscode' | 'faq' | 'rsvp' | 'tv-gallery' | 'hotel-schedule';

interface ContentRegistryProps {
    contentId: ContentId;
    onClose?: () => void;
    isScattering?: boolean;
}

/**
 * Registry mapping contentId to React components
 */
const showContainerDebug = false; // toggle to hide size label

export function ContentRegistry({ contentId, onClose, isScattering = false }: ContentRegistryProps) {
    // Wrap all lazy-loaded content in Suspense
    const renderContent = () => {
        switch (contentId) {
            case 'dresscode':
                return (
                    <ContentContainer showDebugSize={showContainerDebug}>
                        <DressCode onBack={onClose} />
                    </ContentContainer>
                );
            case 'faq':
                return (
                    <ContentContainer showDebugSize={showContainerDebug}>
                        <FAQ onBack={onClose} />
                    </ContentContainer>
                );
            case 'rsvp':
                return (
                    <ContentContainer showDebugSize={showContainerDebug}>
                        <SlideRSVPFlow endpointUrl="/api/rsvp" onClose={onClose} />
                    </ContentContainer>
                );
            case 'tv-gallery': {
                // Filter to items with dimensions for stable layout
                // Only show engagement gallery images (engagementMediaThumbnails already filters for complete pairs with dimensions)
                const masonryItems = engagementMediaThumbnails.filter(
                    item => item.width && item.height,
                );

                return (
                    <ContentContainer $overflowY="auto" showDebugSize={showContainerDebug}>
                        <PageHeader onBack={onClose} />
                        <EngagementMasonry items={masonryItems} />
                    </ContentContainer>
                );
            }
            case 'hotel-schedule':
                return (
                    <ContentContainer showDebugSize={showContainerDebug}>
                        <HotelsPage onBack={onClose} />
                    </ContentContainer>
                );
            default:
                return null;
        }
    };

    return (
        <Suspense fallback={<LoadingFallback />}>
            {renderContent()}
        </Suspense>
    );
}

interface ContentSurfaceProps {
    $paddingTop?: string | undefined;
    $padding?: string | undefined;
    $maxWidth?: string | undefined;
    $overflowY?: 'scroll' | 'auto' | 'hidden' | 'visible' | undefined;
}

const ContentSurface = styled.div<ContentSurfaceProps>`
    width: 100%; 
    height: 100%;
    position: relative;
    display: flex;
    justify-content: center;
    overflow: hidden;
    background: transparent;
`;

const ContentBody = styled.div<ContentSurfaceProps>`
    width: 100%;
    min-width: 0; /* Allow shrinking in flex context */
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow-x: visible;
    overflow-y: ${({ $overflowY }) => $overflowY || 'auto'};
    padding-top: ${({ $paddingTop }) => $paddingTop || '16px'};
    padding-right: 16px;
    padding-left: 16px;
    padding-bottom: 0px;
    box-sizing: border-box;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const ContentInner = styled.div<ContentSurfaceProps>`
    width: 100%;
    min-width: 0; /* Allow shrinking in flex context */
    max-width: ${({ $maxWidth }) => $maxWidth || 'none'};
    margin: 0 auto;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-sizing: border-box;
    flex: 1;
    min-height: 0;
`;

const DebugLabel = styled.div`
    position: absolute;
    top: 6px;
    left: 6px;
    padding: 4px 8px;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    font-size: 12px;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    border-radius: 6px;
    pointer-events: none;
    z-index: 9999;
`;

// Loading fallback for lazy-loaded content
const LoadingFallback = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
`;

interface ContentContainerProps extends ContentSurfaceProps {
    children: React.ReactNode;
    showDebugSize?: boolean;
}

function ContentContainer({ children, showDebugSize = false, ...props }: ContentContainerProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [size, setSize] = useState({ width: 0, height: 0 });

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el || !showDebugSize) return;

        const update = () => {
            const { width, height } = el.getBoundingClientRect();
            setSize({ width: Math.round(width), height: Math.round(height) });
        };

        update();

        const observer = new ResizeObserver(() => update());
        observer.observe(el);

        return () => observer.disconnect();
    }, [showDebugSize]);

    return (
        <ContentSurface ref={containerRef} {...props}>
            {showDebugSize ? (
                <DebugLabel>
                    {size.width}px × {size.height}px
                </DebugLabel>
            ) : null}
            <ContentBody {...props}>
                <ContentInner {...props}>{children}</ContentInner>
            </ContentBody>
        </ContentSurface>
    );
}
